// Pure (Three-free) chunk geometry compute: greedy faces + merged grid lines +
// silhouette edge lines for one chunk, plus the interior-skip / full-chunk
// shell-only fast paths. Returns plain number arrays, so it runs unchanged in a
// Web Worker (which owns a mirror VoxelGrid) and posts the buffers back to the
// main thread, where VoxelMesher applies them to Three.js. Byte-identical to the
// inline version it was extracted from — voxel-mesh.test.ts is the oracle.

import {
  CELL_SIZE,
  CHUNK_AREA,
  CHUNK_SIZE,
  CHUNK_VOLUME,
  type ChunkStorage,
  type PaletteIndex,
  type VoxelGrid,
  parseChunkKey,
} from "../model/voxel-grid";

// A full chunk whose 6 face-neighbour chunks are also full has no exposed faces
// anywhere — every cell's neighbours are occupied — so it renders nothing (no
// faces, grid, or silhouette lines). Detecting this with 6 O(1) chunk lookups
// lets the rebuild skip all the per-cell passes for the bulk of a solid model's
// interior. A later edit that clears a neighbour re-dirties this chunk (the
// grid marks boundary neighbours dirty), so it rebuilds with geometry then.
export function isInteriorChunk(
  grid: VoxelGrid,
  cx: number,
  cy: number,
  cz: number,
  chunk: ChunkStorage,
): boolean {
  if (chunk.count !== CHUNK_VOLUME) return false;
  const full = (c: ChunkStorage | undefined): boolean =>
    c !== undefined && c.count === CHUNK_VOLUME;
  return (
    full(grid.getChunkStorage(cx - 1, cy, cz)) &&
    full(grid.getChunkStorage(cx + 1, cy, cz)) &&
    full(grid.getChunkStorage(cx, cy - 1, cz)) &&
    full(grid.getChunkStorage(cx, cy + 1, cz)) &&
    full(grid.getChunkStorage(cx, cy, cz - 1)) &&
    full(grid.getChunkStorage(cx, cy, cz + 1))
  );
}

// Fast occupancy check for the chunk-rebuild hot path. Most face /
// silhouette neighbor lookups during a chunk rebuild target cells in
// the SAME chunk being rebuilt, so we index the chunk's Uint8Array
// directly for those (arithmetic only — no Map lookup, no key hash).
// Lookups that fall outside the chunk's [0, CHUNK_SIZE) local range
// fall back to grid.has(), which does the chunks Map lookup.
function isOccupied(
  grid: VoxelGrid,
  chunk: ChunkStorage,
  baseX: number,
  baseY: number,
  baseZ: number,
  ix: number,
  iy: number,
  iz: number,
): boolean {
  const lx = ix - baseX;
  const ly = iy - baseY;
  const lz = iz - baseZ;
  if (
    lx >= 0 &&
    lx < CHUNK_SIZE &&
    ly >= 0 &&
    ly < CHUNK_SIZE &&
    lz >= 0 &&
    lz < CHUNK_SIZE
  ) {
    return chunk.cells[lx * CHUNK_AREA + ly * CHUNK_SIZE + lz] !== 0;
  }
  return grid.has(ix, iy, iz);
}

export interface QuadBucket {
  pos: number[];
  norm: number[];
  idx: number[];
}

export interface LineBucket {
  pos: number[];
}

// Edge layout per face: for each of the 4 edges of the face quad, the
// in-plane neighbor cell offset (in cell coords) used by the silhouette
// test. The 4 edges follow the V0→V1→V2→V3→V0 winding of the quad's
// vertices in CCW-from-outside order.
//
// Silhouette rule for one edge: let C be the face's cell and C' be the
// in-plane neighbor across this edge. Let N be the face's outward
// normal vector (one of ±1 along one axis). The edge is silhouette iff
// C' is empty OR (C' is solid AND C' + N is also solid). Reasoning:
//   • C' empty → model's outer boundary at this edge.
//   • C' solid + C'.N exposed → flat continuation, NOT silhouette.
//   • C' solid + C'.N blocked → a step up, side face exposed, silhouette.

function silhouetteAt(
  grid: VoxelGrid,
  chunk: ChunkStorage,
  baseX: number,
  baseY: number,
  baseZ: number,
  cx: number,
  cy: number,
  cz: number,
  nx: number,
  ny: number,
  nz: number,
): boolean {
  if (!isOccupied(grid, chunk, baseX, baseY, baseZ, cx, cy, cz)) {
    return true; // model boundary
  }
  if (
    isOccupied(
      grid,
      chunk,
      baseX,
      baseY,
      baseZ,
      cx + nx,
      cy + ny,
      cz + nz,
    )
  ) {
    return true; // step up
  }
  return false; // flat continuation
}

function pushLine(
  out: LineBucket,
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
): void {
  out.pos.push(ax, ay, az, bx, by, bz);
}

// Pass `out = null` to skip the face-quad emission (caller is handling
// faces via greedy meshing) and still get the per-cell grid + edge
// line emission. With null, this becomes a lines-only pass.
//
// `chunk` + base coords let the per-face neighbor checks index the
// chunk's Uint8Array directly for in-chunk lookups, which is the bulk
// of work during chunk rebuild.
function emitExposedFaces(
  grid: VoxelGrid,
  chunk: ChunkStorage,
  baseX: number,
  baseY: number,
  baseZ: number,
  ix: number,
  iy: number,
  iz: number,
  out: QuadBucket | null,
  gridLines: LineBucket | null,
  edgeLines: LineBucket,
): void {
  const x0 = ix * CELL_SIZE;
  const x1 = x0 + CELL_SIZE;
  const y0 = iy * CELL_SIZE;
  const y1 = y0 + CELL_SIZE;
  const z0 = iz * CELL_SIZE;
  const z1 = z0 + CELL_SIZE;

  // For each face: emit the quad, the 4 grid-line edges, and any
  // silhouette edges. Vertex order is CCW from outside (handled by the
  // index pattern in addQuad).

  // -X face. Verts: (x0,y0,z0), (x0,y1,z0), (x0,y1,z1), (x0,y0,z1).
  // Edges V0V1 (+Y, perp -Z), V1V2 (+Z, perp +Y), V2V3 (-Y, perp +Z), V3V0 (-Z, perp -Y).
  if (!isOccupied(grid, chunk, baseX, baseY, baseZ, ix - 1, iy, iz)) {
    if (out)
      addQuad(out, x0, y0, z0, x0, y1, z0, x0, y1, z1, x0, y0, z1, -1, 0, 0);
    if (gridLines) {
      pushLine(gridLines, x0, y0, z0, x0, y1, z0);
      pushLine(gridLines, x0, y1, z0, x0, y1, z1);
      pushLine(gridLines, x0, y1, z1, x0, y0, z1);
      pushLine(gridLines, x0, y0, z1, x0, y0, z0);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix, iy, iz - 1, -1, 0, 0)) {
      pushLine(edgeLines, x0, y0, z0, x0, y1, z0);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix, iy + 1, iz, -1, 0, 0)) {
      pushLine(edgeLines, x0, y1, z0, x0, y1, z1);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix, iy, iz + 1, -1, 0, 0)) {
      pushLine(edgeLines, x0, y1, z1, x0, y0, z1);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix, iy - 1, iz, -1, 0, 0)) {
      pushLine(edgeLines, x0, y0, z1, x0, y0, z0);
    }
  }

  // +X face. Verts: (x1,y0,z1), (x1,y1,z1), (x1,y1,z0), (x1,y0,z0).
  // Edges V0V1 (+Y, perp +Z), V1V2 (-Z, perp +Y), V2V3 (-Y, perp -Z), V3V0 (+Z, perp -Y).
  if (!isOccupied(grid, chunk, baseX, baseY, baseZ, ix + 1, iy, iz)) {
    if (out)
      addQuad(out, x1, y0, z1, x1, y1, z1, x1, y1, z0, x1, y0, z0, 1, 0, 0);
    if (gridLines) {
      pushLine(gridLines, x1, y0, z1, x1, y1, z1);
      pushLine(gridLines, x1, y1, z1, x1, y1, z0);
      pushLine(gridLines, x1, y1, z0, x1, y0, z0);
      pushLine(gridLines, x1, y0, z0, x1, y0, z1);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix, iy, iz + 1, 1, 0, 0)) {
      pushLine(edgeLines, x1, y0, z1, x1, y1, z1);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix, iy + 1, iz, 1, 0, 0)) {
      pushLine(edgeLines, x1, y1, z1, x1, y1, z0);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix, iy, iz - 1, 1, 0, 0)) {
      pushLine(edgeLines, x1, y1, z0, x1, y0, z0);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix, iy - 1, iz, 1, 0, 0)) {
      pushLine(edgeLines, x1, y0, z0, x1, y0, z1);
    }
  }

  // -Y face. Verts: (x0,y0,z1), (x1,y0,z1), (x1,y0,z0), (x0,y0,z0).
  // Edges V0V1 (+X, perp +Z), V1V2 (-Z, perp +X), V2V3 (-X, perp -Z), V3V0 (+Z, perp -X).
  if (!isOccupied(grid, chunk, baseX, baseY, baseZ, ix, iy - 1, iz)) {
    if (out)
      addQuad(out, x0, y0, z1, x1, y0, z1, x1, y0, z0, x0, y0, z0, 0, -1, 0);
    if (gridLines) {
      pushLine(gridLines, x0, y0, z1, x1, y0, z1);
      pushLine(gridLines, x1, y0, z1, x1, y0, z0);
      pushLine(gridLines, x1, y0, z0, x0, y0, z0);
      pushLine(gridLines, x0, y0, z0, x0, y0, z1);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix, iy, iz + 1, 0, -1, 0)) {
      pushLine(edgeLines, x0, y0, z1, x1, y0, z1);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix + 1, iy, iz, 0, -1, 0)) {
      pushLine(edgeLines, x1, y0, z1, x1, y0, z0);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix, iy, iz - 1, 0, -1, 0)) {
      pushLine(edgeLines, x1, y0, z0, x0, y0, z0);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix - 1, iy, iz, 0, -1, 0)) {
      pushLine(edgeLines, x0, y0, z0, x0, y0, z1);
    }
  }

  // +Y face. Verts: (x0,y1,z0), (x1,y1,z0), (x1,y1,z1), (x0,y1,z1).
  // Edges V0V1 (+X, perp -Z), V1V2 (+Z, perp +X), V2V3 (-X, perp +Z), V3V0 (-Z, perp -X).
  if (!isOccupied(grid, chunk, baseX, baseY, baseZ, ix, iy + 1, iz)) {
    if (out)
      addQuad(out, x0, y1, z0, x1, y1, z0, x1, y1, z1, x0, y1, z1, 0, 1, 0);
    if (gridLines) {
      pushLine(gridLines, x0, y1, z0, x1, y1, z0);
      pushLine(gridLines, x1, y1, z0, x1, y1, z1);
      pushLine(gridLines, x1, y1, z1, x0, y1, z1);
      pushLine(gridLines, x0, y1, z1, x0, y1, z0);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix, iy, iz - 1, 0, 1, 0)) {
      pushLine(edgeLines, x0, y1, z0, x1, y1, z0);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix + 1, iy, iz, 0, 1, 0)) {
      pushLine(edgeLines, x1, y1, z0, x1, y1, z1);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix, iy, iz + 1, 0, 1, 0)) {
      pushLine(edgeLines, x1, y1, z1, x0, y1, z1);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix - 1, iy, iz, 0, 1, 0)) {
      pushLine(edgeLines, x0, y1, z1, x0, y1, z0);
    }
  }

  // -Z face. Verts: (x1,y0,z0), (x1,y1,z0), (x0,y1,z0), (x0,y0,z0).
  // Edges V0V1 (+Y, perp +X), V1V2 (-X, perp +Y), V2V3 (-Y, perp -X), V3V0 (+X, perp -Y).
  if (!isOccupied(grid, chunk, baseX, baseY, baseZ, ix, iy, iz - 1)) {
    if (out)
      addQuad(out, x1, y0, z0, x1, y1, z0, x0, y1, z0, x0, y0, z0, 0, 0, -1);
    if (gridLines) {
      pushLine(gridLines, x1, y0, z0, x1, y1, z0);
      pushLine(gridLines, x1, y1, z0, x0, y1, z0);
      pushLine(gridLines, x0, y1, z0, x0, y0, z0);
      pushLine(gridLines, x0, y0, z0, x1, y0, z0);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix + 1, iy, iz, 0, 0, -1)) {
      pushLine(edgeLines, x1, y0, z0, x1, y1, z0);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix, iy + 1, iz, 0, 0, -1)) {
      pushLine(edgeLines, x1, y1, z0, x0, y1, z0);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix - 1, iy, iz, 0, 0, -1)) {
      pushLine(edgeLines, x0, y1, z0, x0, y0, z0);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix, iy - 1, iz, 0, 0, -1)) {
      pushLine(edgeLines, x0, y0, z0, x1, y0, z0);
    }
  }

  // +Z face. Verts: (x0,y0,z1), (x0,y1,z1), (x1,y1,z1), (x1,y0,z1).
  // Edges V0V1 (+Y, perp -X), V1V2 (+X, perp +Y), V2V3 (-Y, perp +X), V3V0 (-X, perp -Y).
  if (!isOccupied(grid, chunk, baseX, baseY, baseZ, ix, iy, iz + 1)) {
    if (out)
      addQuad(out, x0, y0, z1, x0, y1, z1, x1, y1, z1, x1, y0, z1, 0, 0, 1);
    if (gridLines) {
      pushLine(gridLines, x0, y0, z1, x0, y1, z1);
      pushLine(gridLines, x0, y1, z1, x1, y1, z1);
      pushLine(gridLines, x1, y1, z1, x1, y0, z1);
      pushLine(gridLines, x1, y0, z1, x0, y0, z1);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix - 1, iy, iz, 0, 0, 1)) {
      pushLine(edgeLines, x0, y0, z1, x0, y1, z1);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix, iy + 1, iz, 0, 0, 1)) {
      pushLine(edgeLines, x0, y1, z1, x1, y1, z1);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix + 1, iy, iz, 0, 0, 1)) {
      pushLine(edgeLines, x1, y1, z1, x1, y0, z1);
    }
    if (silhouetteAt(grid, chunk, baseX, baseY, baseZ, ix, iy - 1, iz, 0, 0, 1)) {
      pushLine(edgeLines, x1, y0, z1, x0, y0, z1);
    }
  }
}

function addQuad(
  out: QuadBucket,
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  cx: number, cy: number, cz: number,
  dx: number, dy: number, dz: number,
  nx: number, ny: number, nz: number,
): void {
  const base = out.pos.length / 3;
  out.pos.push(ax, ay, az, bx, by, bz, cx, cy, cz, dx, dy, dz);
  out.norm.push(nx, ny, nz, nx, ny, nz, nx, ny, nz, nx, ny, nz);
  // Triangulation winds CCW from outside (right-hand rule cross product
  // matches the supplied outward normal) so default backface culling
  // hides interior faces, not visible ones.
  out.idx.push(base, base + 2, base + 1, base, base + 3, base + 2);
}

// ── Greedy meshing ────────────────────────────────────────────────────────
//
// Classic 0fps-style greedy meshing on a per-chunk basis. For each of
// the 6 axial face directions and each cell-thick slab within the
// chunk, build a 16×16 mask of "palette where face is exposed, else
// 0". Walk the mask finding maximal same-palette rectangles and emit
// one quad per rectangle into the per-palette QuadBucket.
//
// Wins are biggest on blocky content (a solid 10×10 wall of one
// palette → 1 quad instead of 100). For sparse fragmented content the
// savings shrink but the algorithm still terminates in O(mask size).
// Cell grid lines are emitted separately and merged into collinear
// runs (see emitMergedGridLines); silhouette edge lines stay per-cell
// (see emitExposedFaces). The visual cell breakdown is preserved —
// greedy meshing only affects the underlying face geometry.

// Reused 3D mask buffer (CHUNK_SIZE³ cells, palette indices 0..255 so
// Uint16 has room to spare). Indexed as [slabLocal * area + u * stride
// + v]. Module-scoped + .fill(0)-reset per axis-sign pass so we don't
// allocate per chunk rebuild.
const SLAB_AREA = CHUNK_SIZE * CHUNK_SIZE;
const _greedyMask3D = new Uint16Array(CHUNK_SIZE * SLAB_AREA);
const _slabHasAny = new Uint8Array(CHUNK_SIZE);
// Flat (ix, iy, iz, p) records of the current chunk's filled cells, built once
// per rebuild by rebuildChunk (direct Uint8Array scan) and passed to the face,
// grid, and edge passes so the chunk is walked a single time, not three.
const _chunkCellsBuf: number[] = [];

// Maps a chunk-local cell coordinate triple to global cell indices
// given the slab axis and the in-plane (u, v) interpretation:
//   axis 0 (X-normal): u = Y, v = Z
//   axis 1 (Y-normal): u = X, v = Z
//   axis 2 (Z-normal): u = X, v = Y
function emitGreedyFaces(
  grid: VoxelGrid,
  chunk: ChunkStorage,
  cx: number,
  cy: number,
  cz: number,
  cellsBuf: number[],
  buckets: Map<PaletteIndex, QuadBucket>,
): void {
  const baseX = cx * CHUNK_SIZE;
  const baseY = cy * CHUNK_SIZE;
  const baseZ = cz * CHUNK_SIZE;

  for (const axis of [0, 1, 2] as const) {
    const slabBase = axis === 0 ? baseX : axis === 1 ? baseY : baseZ;
    const uBase = axis === 0 ? baseY : baseX;
    const vBase = axis === 2 ? baseY : baseZ;

    for (const sign of [-1, 1] as const) {
      _greedyMask3D.fill(0);
      _slabHasAny.fill(0);

      // Pass 1: populate the 3D mask from the chunk's filled cells.
      // Only cells whose face on this side is exposed (neighbor cell
      // empty) make it into the mask.
      for (let i = 0; i < cellsBuf.length; i += 4) {
        const ix = cellsBuf[i];
        const iy = cellsBuf[i + 1];
        const iz = cellsBuf[i + 2];
        const p = cellsBuf[i + 3];

        let nx = ix;
        let ny = iy;
        let nz = iz;
        if (axis === 0) nx += sign;
        else if (axis === 1) ny += sign;
        else nz += sign;
        if (isOccupied(grid, chunk, baseX, baseY, baseZ, nx, ny, nz)) continue;

        const slabLocal =
          (axis === 0 ? ix : axis === 1 ? iy : iz) - slabBase;
        const u = (axis === 0 ? iy : ix) - uBase;
        const v = (axis === 2 ? iy : iz) - vBase;

        _greedyMask3D[slabLocal * SLAB_AREA + u * CHUNK_SIZE + v] = p;
        _slabHasAny[slabLocal] = 1;
      }

      // Pass 2: greedy-walk only the slabs that had any face. Empty
      // slabs (the common case in sparse chunks) are skipped wholesale.
      for (let slabLocal = 0; slabLocal < CHUNK_SIZE; slabLocal++) {
        if (!_slabHasAny[slabLocal]) continue;
        const slabGlobal = slabBase + slabLocal;
        const faceCoord = (slabGlobal + (sign > 0 ? 1 : 0)) * CELL_SIZE;
        const slabBaseIdx = slabLocal * SLAB_AREA;

        for (let u = 0; u < CHUNK_SIZE; u++) {
          for (let v = 0; v < CHUNK_SIZE; v++) {
            const p = _greedyMask3D[slabBaseIdx + u * CHUNK_SIZE + v];
            if (p === 0) continue;

            // Width along v (the inner axis).
            let vRun = 1;
            while (
              v + vRun < CHUNK_SIZE &&
              _greedyMask3D[slabBaseIdx + u * CHUNK_SIZE + v + vRun] ===
                p
            ) {
              vRun++;
            }

            // Height along u: extend as long as every cell in the
            // current v-strip matches.
            let uRun = 1;
            uExt: while (u + uRun < CHUNK_SIZE) {
              for (let dv = 0; dv < vRun; dv++) {
                if (
                  _greedyMask3D[
                    slabBaseIdx + (u + uRun) * CHUNK_SIZE + v + dv
                  ] !== p
                ) {
                  break uExt;
                }
              }
              uRun++;
            }

            let bucket = buckets.get(p);
            if (!bucket) {
              bucket = { pos: [], norm: [], idx: [] };
              buckets.set(p, bucket);
            }

            const uMinW = (uBase + u) * CELL_SIZE;
            const uMaxW = (uBase + u + uRun) * CELL_SIZE;
            const vMinW = (vBase + v) * CELL_SIZE;
            const vMaxW = (vBase + v + vRun) * CELL_SIZE;

            emitGreedyQuad(
              bucket,
              axis,
              sign,
              faceCoord,
              uMinW,
              uMaxW,
              vMinW,
              vMaxW,
            );

            // Mark the rectangle as processed.
            for (let du = 0; du < uRun; du++) {
              for (let dv = 0; dv < vRun; dv++) {
                _greedyMask3D[
                  slabBaseIdx + (u + du) * CHUNK_SIZE + v + dv
                ] = 0;
              }
            }
          }
        }
      }
    }
  }
}

// Emit a single greedy quad. The vertex order per face direction
// matches the per-cell addQuad calls in emitExposedFaces above so the
// shared addQuad index pattern produces correctly-wound triangles.
function emitGreedyQuad(
  out: QuadBucket,
  axis: 0 | 1 | 2,
  sign: -1 | 1,
  faceCoord: number,
  uMin: number,
  uMax: number,
  vMin: number,
  vMax: number,
): void {
  if (axis === 0) {
    if (sign < 0) {
      addQuad(
        out,
        faceCoord, uMin, vMin,
        faceCoord, uMax, vMin,
        faceCoord, uMax, vMax,
        faceCoord, uMin, vMax,
        -1, 0, 0,
      );
    } else {
      addQuad(
        out,
        faceCoord, uMin, vMax,
        faceCoord, uMax, vMax,
        faceCoord, uMax, vMin,
        faceCoord, uMin, vMin,
        1, 0, 0,
      );
    }
  } else if (axis === 1) {
    if (sign < 0) {
      addQuad(
        out,
        uMin, faceCoord, vMax,
        uMax, faceCoord, vMax,
        uMax, faceCoord, vMin,
        uMin, faceCoord, vMin,
        0, -1, 0,
      );
    } else {
      addQuad(
        out,
        uMin, faceCoord, vMin,
        uMax, faceCoord, vMin,
        uMax, faceCoord, vMax,
        uMin, faceCoord, vMax,
        0, 1, 0,
      );
    }
  } else {
    if (sign < 0) {
      addQuad(
        out,
        uMax, vMin, faceCoord,
        uMax, vMax, faceCoord,
        uMin, vMax, faceCoord,
        uMin, vMin, faceCoord,
        0, 0, -1,
      );
    } else {
      addQuad(
        out,
        uMin, vMin, faceCoord,
        uMin, vMax, faceCoord,
        uMax, vMax, faceCoord,
        uMax, vMin, faceCoord,
        0, 0, 1,
      );
    }
  }
}

// ── Merged grid lines ──────────────────────────────────────────────────────
//
// The cell-boundary grid drawn on a model's visible surface. Naively each
// exposed face emits its own 4 unit edges, so a flat NxN face emits ~4N² tiny
// segments — and every shared interior edge twice. Here we instead build, per
// chunk slab-face plane, a 16×16 mask of exposed faces, then emit each grid
// line as a maximal run of collinear adjacent edges. The drawn pixels are
// identical (a run of unit segments looks the same as the line covering them);
// only the vertex count drops — the dominant line cost at high density.
//
// A grid edge is drawn iff at least one of the two in-plane faces it borders is
// exposed — exactly the union the per-cell emitter produced. Edges parallel to
// u (at a constant-v boundary) and edges parallel to v (at a constant-u
// boundary) are the two families walked below.

const _gridMask = new Uint8Array(CHUNK_SIZE * SLAB_AREA);
const _gridSlabHasAny = new Uint8Array(CHUNK_SIZE);

// Map an in-plane (u, v) world position on a slab plane to a 3D point, matching
// emitGreedyQuad's per-axis layout, and push the segment p0→p1 into `out`.
function pushGridSeg(
  out: LineBucket,
  axis: 0 | 1 | 2,
  faceCoord: number,
  uA: number, vA: number,
  uB: number, vB: number,
): void {
  if (axis === 0) {
    out.pos.push(faceCoord, uA, vA, faceCoord, uB, vB);
  } else if (axis === 1) {
    out.pos.push(uA, faceCoord, vA, uB, faceCoord, vB);
  } else {
    out.pos.push(uA, vA, faceCoord, uB, vB, faceCoord);
  }
}

function emitMergedGridLines(
  grid: VoxelGrid,
  chunk: ChunkStorage,
  cx: number,
  cy: number,
  cz: number,
  cellsBuf: number[],
  out: LineBucket,
): void {
  const baseX = cx * CHUNK_SIZE;
  const baseY = cy * CHUNK_SIZE;
  const baseZ = cz * CHUNK_SIZE;

  for (const axis of [0, 1, 2] as const) {
    const slabBase = axis === 0 ? baseX : axis === 1 ? baseY : baseZ;
    const uBase = axis === 0 ? baseY : baseX;
    const vBase = axis === 2 ? baseY : baseZ;

    // Chunk-seam dedup: a chunk's FAR u/v boundary line is the same world line as
    // the +neighbour chunk's NEAR (0) boundary. Draw the far line only when that
    // neighbour chunk is absent (a real model edge); otherwise the neighbour owns
    // it. Without this each seam is drawn by both chunks and blends to a darker
    // grid line every 16 cells.
    const uNbr =
      axis === 0
        ? grid.getChunkStorage(cx, cy + 1, cz) !== undefined
        : grid.getChunkStorage(cx + 1, cy, cz) !== undefined;
    const vNbr =
      axis === 2
        ? grid.getChunkStorage(cx, cy + 1, cz) !== undefined
        : grid.getChunkStorage(cx, cy, cz + 1) !== undefined;

    for (const sign of [-1, 1] as const) {
      _gridMask.fill(0);
      _gridSlabHasAny.fill(0);

      // Mark every exposed face (neighbor in this direction empty). The shared
      // snapshot has stride 4 (ix, iy, iz, palette); the palette is unused here.
      for (let i = 0; i < cellsBuf.length; i += 4) {
        const ix = cellsBuf[i];
        const iy = cellsBuf[i + 1];
        const iz = cellsBuf[i + 2];
        let nx = ix;
        let ny = iy;
        let nz = iz;
        if (axis === 0) nx += sign;
        else if (axis === 1) ny += sign;
        else nz += sign;
        if (isOccupied(grid, chunk, baseX, baseY, baseZ, nx, ny, nz)) continue;

        const slabLocal = (axis === 0 ? ix : axis === 1 ? iy : iz) - slabBase;
        const u = (axis === 0 ? iy : ix) - uBase;
        const v = (axis === 2 ? iy : iz) - vBase;
        _gridMask[slabLocal * SLAB_AREA + u * CHUNK_SIZE + v] = 1;
        _gridSlabHasAny[slabLocal] = 1;
      }

      for (let slabLocal = 0; slabLocal < CHUNK_SIZE; slabLocal++) {
        if (!_gridSlabHasAny[slabLocal]) continue;
        const slabGlobal = slabBase + slabLocal;
        const faceCoord = (slabGlobal + (sign > 0 ? 1 : 0)) * CELL_SIZE;
        const base = slabLocal * SLAB_AREA;
        const at = (u: number, v: number): number =>
          u < 0 || u >= CHUNK_SIZE || v < 0 || v >= CHUNK_SIZE
            ? 0
            : _gridMask[base + u * CHUNK_SIZE + v];

        // Family A — lines parallel to u, one per v-boundary (vb in 0..16).
        // The edge at (u, vb) exists iff face (u, vb-1) or (u, vb) is exposed.
        for (let vb = 0; vb <= CHUNK_SIZE; vb++) {
          if (vb === CHUNK_SIZE && vNbr) continue; // seam owned by +v neighbour
          const vWorld = (vBase + vb) * CELL_SIZE;
          let u = 0;
          while (u < CHUNK_SIZE) {
            if (!(at(u, vb - 1) || at(u, vb))) {
              u++;
              continue;
            }
            const uStart = u;
            while (u < CHUNK_SIZE && (at(u, vb - 1) || at(u, vb))) u++;
            pushGridSeg(
              out, axis, faceCoord,
              (uBase + uStart) * CELL_SIZE, vWorld,
              (uBase + u) * CELL_SIZE, vWorld,
            );
          }
        }

        // Family B — lines parallel to v, one per u-boundary (ub in 0..16).
        for (let ub = 0; ub <= CHUNK_SIZE; ub++) {
          if (ub === CHUNK_SIZE && uNbr) continue; // seam owned by +u neighbour
          const uWorld = (uBase + ub) * CELL_SIZE;
          let v = 0;
          while (v < CHUNK_SIZE) {
            if (!(at(ub - 1, v) || at(ub, v))) {
              v++;
              continue;
            }
            const vStart = v;
            while (v < CHUNK_SIZE && (at(ub - 1, v) || at(ub, v))) v++;
            pushGridSeg(
              out, axis, faceCoord,
              uWorld, (vBase + vStart) * CELL_SIZE,
              uWorld, (vBase + v) * CELL_SIZE,
            );
          }
        }
      }
    }
  }
}

export interface ChunkGeometry {
  faces: Map<PaletteIndex, QuadBucket>;
  gridLines: number[];
  edgeLines: number[];
}

// Compute one chunk's render geometry. Returns null when the chunk has nothing
// to draw (no cells, or fully interior to a solid). The greedy face buckets,
// merged grid lines, and silhouette edge lines are produced exactly as the
// inline rebuild did, sharing a single direct Uint8Array scan.
// facesOnly=true is the DRAFT mesh used while a high-volume edit is dragging:
// solid greedy faces only, skipping the grid-line and silhouette-edge passes
// (the bulk of the per-chunk compute). The shape reads correctly; the lines fill
// in when the worker re-meshes at full detail on settle.
export function buildChunkGeometry(
  grid: VoxelGrid,
  ck: number,
  facesOnly = false,
  withGridLines = true,
): ChunkGeometry | null {
  const [cx, cy, cz] = parseChunkKey(ck);
  const chunk = grid.getChunkStorage(cx, cy, cz);
  if (!chunk || chunk.count === 0 || isInteriorChunk(grid, cx, cy, cz, chunk)) {
    return null;
  }

  const baseX = cx * CHUNK_SIZE;
  const baseY = cy * CHUNK_SIZE;
  const baseZ = cz * CHUNK_SIZE;

  const cellsBuf = _chunkCellsBuf;
  cellsBuf.length = 0;
  const cells = chunk.cells;
  const shellOnly = chunk.count === CHUNK_VOLUME;
  const EDGE = CHUNK_SIZE - 1;
  for (let lx = 0; lx < CHUNK_SIZE; lx++) {
    const gx = baseX + lx;
    const xBase = lx * CHUNK_AREA;
    const xEdge = lx === 0 || lx === EDGE;
    for (let ly = 0; ly < CHUNK_SIZE; ly++) {
      const gy = baseY + ly;
      const rowBase = xBase + ly * CHUNK_SIZE;
      const xyEdge = xEdge || ly === 0 || ly === EDGE;
      for (let lz = 0; lz < CHUNK_SIZE; lz++) {
        if (shellOnly && !xyEdge && lz !== 0 && lz !== EDGE) continue;
        const p = cells[rowBase + lz];
        if (p !== 0) cellsBuf.push(gx, gy, baseZ + lz, p);
      }
    }
  }

  const faces = new Map<PaletteIndex, QuadBucket>();
  const gridLines: LineBucket = { pos: [] };
  const edgeLines: LineBucket = { pos: [] };

  emitGreedyFaces(grid, chunk, cx, cy, cz, cellsBuf, faces);
  if (!facesOnly) {
    for (let i = 0; i < cellsBuf.length; i += 4) {
      emitExposedFaces(
        grid, chunk, baseX, baseY, baseZ,
        cellsBuf[i], cellsBuf[i + 1], cellsBuf[i + 2],
        null, null, edgeLines,
      );
    }
    // Grid lines are a separate, costly pass (6 sweeps over the shell cells per
    // chunk). The placement bake hides grid lines, so it skips this entirely.
    if (withGridLines) emitMergedGridLines(grid, chunk, cx, cy, cz, cellsBuf, gridLines);
  }

  return { faces, gridLines: gridLines.pos, edgeLines: edgeLines.pos };
}
